import { CommonModule } from '@angular/common';
import { Component, signal, ViewChild, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { AttachmentViewerComponent } from '@app/lib/components/attachment-viewer/attachment-viewer.component';
import { BreadcrumbComponent } from '@app/lib/components/breadcrumb/breadcrumb.component';
import { TAny, TIdName } from '@app/lib/models/common.type';
import { IResponse } from '@app/lib/models/response.model';
import { LoaderComponent } from '@lib/components/loader/loader.component';
import { IBreadcrumb } from '@lib/models/breadcrumb.model';
import { IAssignCourt, IAvaiableJudgeParams, ICaseItem, ICaseParty, ICasePartyParams, ICaseSearchRequestBody, ICaseTrailCourt, ICaseTrailCourtRequestBody, ICourtDivisionParams, ICourtSessionJudgeParams, ICourtSessionPanel, ICourtSessionPanelParams, ICourtUnitType, IJudge, IJudgeStat, IJudgeStatBody, IJudgeStatData, IMotionCaseNumber, IPagination, IPartyTypeModel, ISearchRequestParams } from "@models/case-assignment.model";
import { debounceTime, filter, switchMap } from 'rxjs';
import { CaseDocumentsComponent } from '../case-documents/case-documents.component';
import { ETaskScreenName, IAssignedTask, IAssignedTaskEvent } from '../models/assigned-task.model';
import { ECaseCategory, ECasePartyRoleType, TOTAL_ITEMS } from '../models/constant';
import { AssignedTaskService } from '../services/assigned-task.service';
import { CourtPanelAssignmentService } from '../services/court-panel-assignment.service';
import { DialogService } from '@app/lib/services/dialog.service';
import { ILoginData } from '@app/lib/models/login.model';
import { DialogMessageComponent } from '../dialog-message/dialog-message.component';
import { ConfirmationDialogComponent } from '@app/confirmation-dialog/confirmation-dialog.component';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { DashboardComponent } from '../dashboard.component';
import { CaseNumberSearchComponent } from '../widget/case-number-search/case-number-search.component';
import { AppConstantsService } from '@app/dashboard/services/app-constants.service';

@Component({
  selector: 'app-court-panel-assignment',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatButtonModule,
    MatTableModule,
    MatDialogModule,
    MatExpansionModule,
    MatAutocompleteModule,
    BreadcrumbComponent,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatProgressSpinnerModule,
    CaseDocumentsComponent,
    LoaderComponent,
    ScrollingModule,
    CaseNumberSearchComponent
  ],
  templateUrl: './court-panel-assignment.component.html',
  styleUrl: './court-panel-assignment.component.scss'
})
export class CourtPanelAssignmentComponent {

  public breadcrumbs: WritableSignal<IBreadcrumb[]> = signal([
    { label: 'Documents' },
    { label: 'Court / Panel Assignment' },
  ]);

  @ViewChild(AttachmentViewerComponent) attachmentViewer!: AttachmentViewerComponent;

  public caseCategoryEnum = ECaseCategory;
  public partyRoleType = ECasePartyRoleType;

  public caseListLoader: WritableSignal<boolean> = signal(false);
  public isAssigned: WritableSignal<boolean> = signal(false);
  public caseList: WritableSignal<ICaseItem[]> = signal([]);
  public divisions: WritableSignal<ICourtUnitType[]> = signal([]);
  public courts: WritableSignal<ICourtUnitType[]> = signal([]);
  public judges: WritableSignal<IJudge[]> = signal([]);
  courtData: ILoginData | null = null;
  public courtSessions: WritableSignal<TIdName[]> = signal([]);
  public panels: WritableSignal<ICourtSessionPanel[]> = signal([]);
  public panelJudges: WritableSignal<IJudge[]> = signal([]);
  public motionCases: WritableSignal<IMotionCaseNumber[]> = signal([]);
  public judgeStatData: WritableSignal<IJudgeStatData | { totalCount: number }> = signal({ totalCount: 0 });
  public partyDetails: WritableSignal<IPartyTypeModel> = signal({
    [this.partyRoleType.APPELLANT]: { value: [], partyData: [] },
    [this.partyRoleType.RESPONDENT]: { value: [], partyData: [] },
    loading: false
  });
  public task: WritableSignal<IAssignedTask | null> = signal(null);
  public searchFormGrp: FormGroup = new FormGroup({
    dateOfFiling: new FormControl(''),
    caseNumber: new FormControl('', { nonNullable: true })
  });
  public formGrp: FormGroup = new FormGroup({
    division: new FormControl('', [Validators.required]),
    court: new FormControl('', [Validators.required]),
    judge: new FormControl(''),
    session: new FormControl(''),
    panel: new FormControl(''),
    comments: new FormControl(''),
  });
  public trailCourtInfo: WritableSignal<ICaseTrailCourt | null> = signal(null)
  public searchedCaseNumber: WritableSignal<string> = signal('');

  public caseAssigned: WritableSignal<boolean> = signal(false);

  public caseCategoryName: WritableSignal<'Case' | 'Appeal'> = signal('Case');

  public loading: WritableSignal<boolean> = signal(false);
  judgeLoading: boolean = false;
  editDisabled = false;
  userType: string = "";

  constructor(
    private service: CourtPanelAssignmentService,
    private assignTaskService: AssignedTaskService,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private dashboard: DashboardComponent,
    public sessionConstant: AppConstantsService,
  ) {
    this.searchFormGrp.controls['caseNumber'].valueChanges
      .pipe(
        takeUntilDestroyed(),
        filter(val => {
          if (typeof val === 'string' && val.trim().length > 3) {
            this.caseListLoader.set(true);
            return true;
          }
          this.caseListLoader.set(false);
          return false;
        }),
        debounceTime(500),
        switchMap((searchVal: string) => this.searchCases(searchVal))
      )
      .subscribe({
        next: (res: IResponse<ICaseItem[]>) => {
          this.formGrp.disable();
          this.caseList.set(res.data);
          this.caseListLoader.set(false);
          this.isAssigned.set(true);
        },
        error: () => {
          this.caseListLoader.set(false);
        }
      });
    this.assignTaskService.assignTask$
      .pipe(
        takeUntilDestroyed(),
        filter(event => event.name === ETaskScreenName.COURTPANEL)
      )
      .subscribe({
        next: (event: IAssignedTaskEvent) => {
          this.isAssigned.set(false);
          this.formGrp.disable();
          this.task.set(event.task);
          this.caseAssigned.set(false);
          this.onCaseNumberSearch(event.task.courtCaseNumber);
        }
      });
  }

  public onCaseSelection(e: MatAutocompleteSelectedEvent) {
    this.task.set(null);
    const selectedCase: ICaseItem = e.option.value;
    this.onCaseNumberSearch(selectedCase.caseNumber);
  }

  public caseDisplayFn(item: ICaseItem) {
    return item && item.caseNumber;
  }



  public sessionChangeHandler(e: MatSelectChange) {
    this.formGrp.patchValue({
      panel: ''
    });
    const reqParams: ICourtSessionPanelParams = {
      page: 1,
      start: 0,
      limit: 25,
      property: 'id',
      direction: 'ASC',
      id: e.value
    }
    this.service.getPanels(reqParams).subscribe({
      next: (res: IResponse<ICourtSessionPanel[]>) => {
        this.panels.set(res.data || res.success || []);
      }
    });
  }
  get sortedCourtList() {
    return this.courts().slice().sort((a, b) => {
      const nameA = a.courtUnitTypeName;
      const nameB = b.courtUnitTypeName;

      const numA = parseInt(nameA.match(/\d+$/)?.[0] || '0', 10);
      const numB = parseInt(nameB.match(/\d+$/)?.[0] || '0', 10);

      const baseA = nameA.replace(/\d+$/, '').trim();
      const baseB = nameB.replace(/\d+$/, '').trim();

      if (baseA === baseB) {
        return numA - numB; // Sort numerically
      }
      return baseA.localeCompare(baseB); // Then alphabetically
    });
  }

  public panelChangeHandler(e: MatSelectChange) {
    const reqParams: ISearchRequestParams = {
      page: 1,
      start: 0,
      limit: 25,
      property: 'id',
      direction: 'ASC',
    };
    const reqBody: ICourtSessionJudgeParams = {
      id: e.value,
      panelSessionId: this.formGrp.value.session || ''
    }
    this.service.getPanelJudges(reqBody, reqParams).subscribe({
      next: (res: IResponse<IJudge[]>) => {
        this.panelJudges.set(res.data || res.success || []);
        if (this.panelJudges().length === 1) { }
      }
    });
  }

  public divisionChangeHandler(e: MatSelectChange) {
    this.formGrp.patchValue({
      court: '',
      session: '',
      panel: '',
      judge: ''
    })
    this.getCourts(e.value);
  }

  public courtChangeHandler(e: MatSelectChange) {
    if (this.trailCourtInfo() !== null && this.trailCourtInfo()?.caseCategory === this.caseCategoryEnum.FI) {
      this.formGrp.patchValue({
        judge: ''
      });
      this.judgeStatData.set({ totalCount: 0 });
      this.getJudges(e.value);
    } else {

    }

  }

  public editHandler() {
    this.caseAssigned.set(true);
    this.formGrp.enable();
  }

  public cancelHandler() {
    this.caseAssigned.set(false);
    this.formGrp.disable();
  }

  getCourtNameData(): ILoginData | null {
    try {
      const userData = localStorage.getItem('userData') as string;
      return userData ? JSON.parse(userData) : null;
    } catch (err) {
      return null
    }
  }
  public onCaseSearchOptionSelected(item: any): void {
    const caseNo = item?.caseNumber || item?.courtCaseNumber || '';
    if (caseNo) {
      this.searchFormGrp.patchValue({ caseNumber: caseNo });
      this.onCaseNumberSearch(caseNo);
    }
  }
  public isOYO(): boolean {
    const courtData = this.getCourtNameData();
    this.userType = this.courtData?.courtId === 1 || this.courtData?.courtId === 2 ? 'admin' : 'lawyer';
    if (courtData && courtData?.courtName) {
      return courtData?.courtName.toLowerCase().includes('oyo');
    } else {
      return false;
    }

  }

  public isIstCourt(): boolean {
    const courtInfo = this.getCourtNameData();
    if (courtInfo && courtInfo.courtName) {
      return courtInfo.courtName.toLowerCase().includes('investments');
    }
    return false;
  }

  public assignHandler() {
    const courtInfo = this.trailCourtInfo();
    this.formGrp.markAllAsTouched();

    if (courtInfo && this.formGrp.valid) {
      const formData = this.formGrp.value;

      const isPanelCategory =
        courtInfo.caseCategory === this.caseCategoryEnum.A ||
        (courtInfo.caseCategory === this.caseCategoryEnum.FI && (this.isIstCourt() || this.isOYO()));

      // 1. Validation for Appeal / Panel flow: At least one panel judge is required
      if (isPanelCategory) {
        if (!formData.panel || !this.panelJudges().length) {
          this.dialogService.openDialog1({
            data: {
              title: 'Validation',
              message: !formData.panel
                ? 'Please select a Panel.'
                : 'At least one judge is required in the selected panel.',
              icon: 'info'
            },
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'dialog-backdrop',
          });
          return; // Stop execution
        }
      }
      // 2. Validation for FI (Single Judge) flow: Judge is required
      else if (courtInfo.caseCategory === this.caseCategoryEnum.FI) {
        if (!formData.judge) {
          this.dialogService.openDialog1({
            data: {
              title: 'Validation',
              message: 'Please select a Judge.',
              icon: 'info'
            },
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'dialog-backdrop',
          });
          return; // Stop execution
        }
      }
      const selectedDivision = this.divisions().find(division => division.courtUnitTypeId === formData.division);
      const selectedCourt = this.courts().find(court => court.courtUnitTypeId === formData.court);
      const selectedJudge = this.judges().find(judge => judge.justiceId === formData.judge);
      const selectedSession = this.courtSessions().find(courtSession => courtSession.id === formData.session);
      const selectedPanel = this.panels().find(panel => panel.id === formData.panel);

      const panelJudges: IJudge[] | undefined =
        courtInfo.caseCategory === 'A' || (courtInfo.caseCategory == this.caseCategoryEnum.FI && (this.isIstCourt() || this.isOYO()))
          ? this.panelJudges().map(judge => ({
            id: 0,
            panelName: "",
            panelId: 0,
            justiceId: judge.justiceId,
            justiceName: judge.justiceName,
            beginDate: null,
            endDate: null,

          }))
          : undefined;

      const reqBody: TAny = {
        'caseId': courtInfo.caseId,
        'courtCaseNumber': courtInfo.courtCaseNumber,
        'caseStatusName': courtInfo.caseStatusName,
        'cptName': courtInfo.cptName,
        'oldDivisionId': courtInfo.oldDivisionId,
        'oldCourtRoomId': courtInfo.oldCourtRoomId,
        'division': selectedDivision?.courtUnitTypeName as string,
        'courtsName': selectedCourt?.courtUnitTypeId as string,
        'justiceId': (selectedJudge?.justiceId || '') as string,
        'sessionName': (selectedSession?.name || '') as string,
        'panelStartDate': courtInfo.panelStartDate,
        'panelEndDate': courtInfo.panelEndDate,
        'triggeredTaskId': this.task()?.tasksId ?? '',
        'courtUnitId': courtInfo.courtUnitId,
        'docId': courtInfo.docId,
        'caseCategory': courtInfo.caseCategory,
        'divisionId': selectedDivision?.courtUnitTypeId ?? 0,
        'hearingDate': courtInfo.hearingDate,
        'casePhId': courtInfo.casePhId,
        'caseStatusId': courtInfo.caseStatusId,
        'cptId': courtInfo.cptId,
        'courtsId': selectedCourt?.courtUnitTypeId as string,
        'sessionBasedId': (selectedSession?.id || '') as string,
        'sessionBasedPanelId': (selectedPanel?.id || '') as string,
        'courtType': this.sessionConstant.COURT_TYPE,
        'comments': formData.comments,
      };
      if (courtInfo.caseCategory === 'A' || (courtInfo.caseCategory == this.caseCategoryEnum.FI && (this.isIstCourt() || this.isOYO()))) {
        reqBody['assignJudgeData'] = this.panelJudges();
        reqBody['sessionBasedPanelName'] = this.getPanelLabelById(selectedPanel?.id ?? 0);
      }

      // Show Confirmation Dialog
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          title: 'Confirm Action',
          message: "Do you want to Assign?"
        },
        disableClose: true,           // Prevents closing on outside click or ESC 
        hasBackdrop: true,            // Shows backdrop to block interaction
        backdropClass: 'dialog-backdrop', // Optional: custom styling
      });

      // Proceed only if the user confirms
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          // Proceed with the API call
          const apiCall = (courtInfo.caseCategory === 'A' || (courtInfo.caseCategory == this.caseCategoryEnum.FI && (this.isIstCourt() || this.isOYO())))
            ? this.service.assignPanel(reqBody)
            : this.service.saveAssignJudge(reqBody);

          apiCall.subscribe({
            next: (res) => {
              if (res.success) {
                this.dialogService.openDialog1({
                  data: {
                    title: "Success",
                    message: res.message || ((courtInfo.caseCategory === 'A' || (courtInfo.caseCategory == this.caseCategoryEnum.FI && (this.isIstCourt() || this.isOYO())))
                      ? 'Panel assigned successfully'
                      : 'Court assigned successfully'),
                    icon: "check_small" // ✅ Success icon
                  }, disableClose: true,           // Prevents closing on outside click or ESC
                  hasBackdrop: true,            // Shows backdrop to block interaction
                  backdropClass: 'dialog-backdrop', // Optional: custom styling
                });
              };
              this.dashboard.refreshSidebarTasks();
            },
            error: (err) => {
              this.dashboard.refreshSidebarTasks();
              console.error(err);
            }
          });

          this.caseAssigned.set(false);
          this.editDisabled = true;
          this.formGrp.disable();

        } else {
          //console.log('Cancelled!'); // Handle cancellation
        }
      });
    }
  }



  private onCaseNumberSearch(caseNumber: string) {
    this.searchedCaseNumber.set(caseNumber);
    this.searchFormGrp.reset();
    this.formGrp.reset();
    this.caseList.set([]);
    this.judges.set([]);
    this.fetchTrailCourt();
    this.editDisabled = false;



    // Check if already in edit mode
    if (this.caseAssigned()) {
      // Reset edit mode to normal mode
      this.cancelHandler(); // Reset the form and disable fields
    }
  }

  ngOnInit(): void {
    this.panelJudges.set([]);
    this.judgeStatData.set({ totalCount: 0 });

  }

  private fetchTrailCourt() {
    this.loading.set(true);
    this.panelJudges.set([]);
    this.judgeStatData.set({ totalCount: 0 });
    const reqBody: ICaseTrailCourtRequestBody = {
      courtCaseNumber: this.searchedCaseNumber()
    };
    this.service.getTrailCount(reqBody).subscribe({
      next: (res: IResponse<ICaseTrailCourt>) => {
        this.trailCourtInfo.set(res.data);
        this.caseCategoryName.set(this.caseCategoryEnum.A === this.trailCourtInfo()?.caseCategory ? 'Appeal' : 'Case');
        this.formGrp.patchValue({
          division: res.data.divisionId,
          court: res.data.courtsId,
          judge: res.data.caseCategory == this.caseCategoryEnum.FI ? res.data.justiceId : '',
          session: res.data.caseCategory == this.caseCategoryEnum.FI ? '' : res.data.sessionId,
          panel: res.data.caseCategory == this.caseCategoryEnum.FI ? '' : res.data.panelId,
        });

        this.getDevisions();
        this.getPartyDetails(this.searchedCaseNumber());
        if (res.data.divisionId) {
          this.getCourts(res.data.divisionId);
        }
        const sessionControl = this.formGrp.get('session');
        const panelControl = this.formGrp.get('panel');
        const judgeControl = this.formGrp.get('judge');

        if (res.data.caseCategory === this.caseCategoryEnum.A || (this.trailCourtInfo()?.caseCategory == this.caseCategoryEnum.FI && (this.isIstCourt() || this.isOYO()))) {
          sessionControl?.setValidators([Validators.required]);
          panelControl?.setValidators([Validators.required]);
          judgeControl?.clearValidators();
          this.getCourtSessions();
        } else {
          judgeControl?.setValidators([Validators.required]);
          sessionControl?.clearValidators();
          panelControl?.clearValidators();
          this.getJudges(res.data.courtsId);
        }

        sessionControl?.updateValueAndValidity();
        panelControl?.updateValueAndValidity();

        this.loading.set(false);
      },
      error: () => {
        this.trailCourtInfo.set(null);
        this.loading.set(false);
      },
      complete: () => {
        if (this.task != null) {
          if (this.trailCourtInfo()?.cptName == "Assigned") {
          }
        }
      }
    });
  }

  private getDevisions() {
    const queryParams: ISearchRequestParams = {
      page: 1,
      start: 0,
      limit: 25,
      property: 'courtUnitTypeId',
      direction: 'ASC',
    };

    this.service.getDivisions(queryParams).subscribe({
      next: (res: IResponse<ICourtUnitType[]>) => {
        const courtData = this.getCourtNameData();
        //console.log(courtData);
        const divisionName = courtData?.divisionName || 'All';
        const userInfo = courtData?.roleDTOList[0].roleShortName
        if (userInfo === 'AJ') {
          if (divisionName.toLowerCase() === 'all') {
            // Filter out the 'All' record itself
            this.divisions.set(res.data?.filter(item =>
              item.courtUnitTypeName.toLowerCase() !== 'all'
            ) || []);
          } else {
            // Filter to match the specific division
            this.divisions.set(res.data?.filter(item =>
              item.courtUnitTypeName.toLowerCase() === divisionName.toLowerCase()
            ) || []);
          }
        }
        else {
          this.divisions.set(res.data?.filter(item =>
            item.courtUnitTypeName.toLowerCase() !== 'all'
          ) || []);
        }
      }
    });
  }

  private getCourts(divisionId: string | null) {
    if (divisionId) {
      const queryParams: ICourtDivisionParams = {
        page: 1,
        start: 0,
        limit: 25,
        property: 'courtUnitTypeId',
        direction: 'ASC',
        courtUnitTypeId: divisionId
      };
      this.service.getCourtUnitByDivision(queryParams).subscribe({
        next: (res: { success: ICourtUnitType[] }) => {
          this.courts.set(res.success.length ? res.success : []);
        }
      });
    }
  }

  private getJudges(courtUnitId: string | null) {
    this.judgeLoading = true;
    if (courtUnitId) {
      const queryParams: IAvaiableJudgeParams = {
        page: 1,
        start: 0,
        limit: 25,
        property: 'justiceName',
        direction: 'ASC',
        courtsNameId: courtUnitId
      };

      this.service.getAvailableJudges(queryParams).subscribe({
        next: (res: IResponse<IJudge[]>) => {
          if (res.data.length > 0) {
            this.judges.set(res.data);
            if (this.judges().length === 1) {
              this.formGrp.patchValue({ judge: this.judges()[0].justiceId });
              this.getJudgeStats(courtUnitId);
            }
            this.judgeLoading = false;
          } else {
            this.judges.set([]); // Clear the judges list
            this.dialogService.openDialog1({
              data: {
                title: 'Information',
                message: 'Judge is not assigned to the selected Court',
                icon: 'info' // ⚠️ Warning icon
              }, disableClose: true,           // Prevents closing on outside click or ESC
              hasBackdrop: true,            // Shows backdrop to block interaction
              backdropClass: 'dialog-backdrop', // Optional: custom styling
            });
            this.judgeLoading = false;
          }
        },
        error: (err) => {
          console.error(err);
        }
      });
    }
  }


  private getJudgeStats(courtsId: string | null) {
    if (courtsId) {
      const reqBody: IJudgeStatBody = {
        courtsNameId: courtsId
      };

      this.service.getJudgeStats(reqBody).subscribe({
        next: (res: IResponse<IJudgeStat[]>) => {
          if (!res.data || res.data.length === 0) {
            this.dialogService.openDialog1({
              data: {
                title: 'Information',
                message: 'Assignment count data not found',
                icon: 'info' // ⚠️ Warning icon
              }, disableClose: true,           // Prevents closing on outside click or ESC
              hasBackdrop: true,            // Shows backdrop to block interaction
              backdropClass: 'dialog-backdrop', // Optional: custom styling
            });
            return;
          }

          const stats = res.data;
          const total = stats.find(item => item.caseTypeShortName === TOTAL_ITEMS);

          this.judgeStatData.update(() => {
            const data: IJudgeStatData = { totalCount: total?.count || 0 };
            stats.forEach(statItem => {
              if (statItem.caseTypeShortName !== TOTAL_ITEMS) {
                data[statItem.caseTypeShortName] = statItem.count || 0;
              }
            });
            return data;
          });
        },
        error: (err) => {
          console.error("Error fetching judge stats:", err);
          // Handle API errors
          this.dialogService.openDialog1({
            data: {
              title: 'Error',
              message: err,
              icon: 'error' // ⚠️ Warning icon
            }, disableClose: true,           // Prevents closing on outside click or ESC
            hasBackdrop: true,            // Shows backdrop to block interaction
            backdropClass: 'dialog-backdrop', // Optional: custom styling
          });
        }
      });
    }
  }


  private getPartyDetails(caseNumber: string) {
    this.partyDetails.update((data) => {
      data.loading = true;
      return data;
    });
    const reqParams: ICasePartyParams = {
      caseNumber
    }
    this.service.getPartyDetails(reqParams).subscribe({
      next: (res: IResponse<ICaseParty[]>) => {
        if (res.data.length) {
          const appellant = res.data.filter(item => item.partyRoleId == this.partyRoleType.APPELLANT);
          const respondant = res.data.filter(item => item.partyRoleId == this.partyRoleType.RESPONDENT);
          this.partyDetails.update((data) => {
            data[this.partyRoleType.APPELLANT] = {
              partyData: appellant,
              value: appellant.map(item => item.fullName)
            };
            data[this.partyRoleType.RESPONDENT] = {
              partyData: respondant,
              value: respondant.map(item => item.fullName)
            };
            data.loading = false;
            return data;
          });
        }
      }
    });
  }

  private getCourtSessions() {
    const queryParams: IPagination = {
      limit: 100,
      page: 1,
      start: 0
    };
    this.service.getCourtSessions(queryParams)
      .subscribe({
        next: (res: IResponse<TIdName[]>) => {
          this.courtSessions.set(res.data || res.success || []);
        }
      });
  }
  getPanelLabelById(panelId: number): string {
    const panel = this.panels().find(p => p.id === panelId);

    if (!panel) return '';

    const formatDate = (dateStr: string) => {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${day}-${month}-${year}`;
    };

    const start = formatDate(panel.activeStartDateStr);
    const end = formatDate(panel.activeEndDateStr);

    return `${panel.name} (${start} to ${end})`;
  }

  private searchCases(val: string) {
    const body: ICaseSearchRequestBody = {
      caseNumber: val,
      dateOfFiling: 'null'
    };
    const params: ISearchRequestParams = {
      page: 1,
      start: 0,
      limit: 25,
      property: 'id',
      direction: 'ASC'
    }
    return this.service.searchCase(body, params);
  }

  private updateFieldValidators(name: string, validators: ValidatorFn[]) {
    const ctrl = this.formGrp.get(name);
    if (ctrl) {
      ctrl.setValidators(validators);
      ctrl.updateValueAndValidity();
    }
  }

}
